import { Controller } from "@hotwired/stimulus"
import { remove_flash } from "lib/helpers"

// notifier--settings on `app/views/notifier/settings/index.haml` (#settings tab).
export default class extends Controller {
  static targets = [
    "uploadCertForm",
    "panelCollapse",
    "fcmServiceAccountForm",
    "certificates",
    "apnsUploadErrors",
    "fcmCredentials",
    "fcmUploadErrors",
  ]

  connect() {
    this.#applyFcmStatusClasses()
    this.#initCollapseCookies()
    this.#initFileBrowse()
    if (this.hasUploadCertFormTarget) this.#initCertUpload()
    if (this.hasFcmServiceAccountFormTarget) this.#initFcmUpload()
  }

  disconnect() {
    if (this.hasUploadCertFormTarget) $(this.uploadCertFormTarget).ajaxFormUnbind()
    if (this.hasFcmServiceAccountFormTarget) $(this.fcmServiceAccountFormTarget).ajaxFormUnbind()
    this.panelCollapseTargets.forEach((el) => $(el).off(".notifierSettings"))
    this.element.querySelectorAll("input.fileName").forEach((el) => $(el).off(".notifierSettings"))
    this.element.querySelectorAll("input.inputFile").forEach((el) => $(el).off(".notifierSettings"))
  }

  showGcmServerKeyForm(event) {
    event.preventDefault()
    $(".btn-fcm-service-account-key").removeClass("btn-active")
    $(".btn-gcm-server-key").addClass("btn-active")
    $(".fcm-setting-form").addClass("hide-form")
    $(".gcm-setting-form").removeClass("hide-form")
  }

  showFcmServiceAccountForm(event) {
    event.preventDefault()
    $(".btn-gcm-server-key").removeClass("btn-active")
    $(".btn-fcm-service-account-key").addClass("btn-active")
    $(".fcm-setting-form").removeClass("hide-form")
    $(".gcm-setting-form").addClass("hide-form")
  }

  confirmFcmUpload(event) {
    event.preventDefault()
    $("#confirmation_fcm_upload_modal").modal("show")
  }

  submitFcmServiceAccount(event) {
    event.preventDefault()
    if (this.hasFcmServiceAccountFormTarget) $(this.fcmServiceAccountFormTarget).submit()
    $("#confirmation_fcm_upload_modal").modal("hide")
  }

  deleteFcmCredential(event) {
    event.preventDefault()

    this.#deleteCredential(event.currentTarget, (message) => this.#showFcmUploadError(message))
  }

  deleteAppleCertificate(event) {
    event.preventDefault()

    this.#deleteCredential(event.currentTarget, (message) => this.#showApnsUploadError(message), this.certificatesTarget)
  }

  #deleteCredential(button, showError, emptyContainer = null) {
    const url = button.dataset.deleteUrl
    if (!url) return

    button.disabled = true
    $.ajax({
      url,
      type: "DELETE",
      dataType: "json",
      headers: {
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "",
        "X-Requested-With": "XMLHttpRequest",
        Accept: "application/json",
      },
      success: () => {
        $(button).closest(".cert").remove()
        if (emptyContainer && emptyContainer.querySelectorAll(".cert").length === 0 && !emptyContainer.querySelector(".no-certificates")) {
          emptyContainer.insertAdjacentHTML("beforeend", '<p class="no-certificates">No certificates</p>')
        }
      },
      error: (response) => {
        button.disabled = false
        const message = response.responseJSON?.message || "An error occurred during request"
        showError(message)
      },
    })
  }

  #initCertUpload() {
    $(this.uploadCertFormTarget).ajaxForm({
      dataType: "json",
      beforeSubmit: () => {
        const form = $(this.uploadCertFormTarget)
        if (!form.find("input[type=file]").val()) {
          remove_flash()
          alert("You must select a file!")
          return false
        }

        remove_flash()
        this.#hideUploadError(this.apnsUploadErrorsTarget)
        form.find("input[type=submit]").prop("disabled", true)
      },
      success: (response) => {
        this.#endCertUpload()
        if (response.success) {
          this.#upsertCertificate(response.certificate)
          this.uploadCertFormTarget.reset()
          $(this.uploadCertFormTarget).find("input.fileName").val("")
          this.#showApnsUploadSuccess(response.message)
        } else {
          this.#showApnsUploadError(response.message)
        }
      },
      error: (response) => {
        this.#endCertUpload()
        const message = response.responseJSON?.message || "An error occurred during request"
        this.#showApnsUploadError(message)
      },
    })
  }

  #initFcmUpload() {
    $(this.fcmServiceAccountFormTarget).ajaxForm({
      dataType: "json",
      beforeSubmit: () => {
        const form = $(this.fcmServiceAccountFormTarget)
        if (!form.find("input[type=file]").val()) {
          remove_flash()
          this.#showFcmUploadError("You must select a file!")
          return false
        }

        remove_flash()
        this.#hideUploadError(this.fcmUploadErrorsTarget)
        form.find("input[type=submit]").prop("disabled", true)
      },
      success: (response) => {
        this.#endFcmUpload()
        this.#upsertFcmCredential(response.credential)
        this.fcmServiceAccountFormTarget.reset()
        $(this.fcmServiceAccountFormTarget).find("input.fileName").val("")
        this.#showFcmUploadSuccess(response.message)
      },
      error: (response) => {
        this.#endFcmUpload()
        const message = response.responseJSON?.message || "An error occurred during request"
        this.#showFcmUploadError(message)
      },
    })
  }

  #endCertUpload() {
    $(this.uploadCertFormTarget).find("input[type=submit]").prop("disabled", false)
  }

  #endFcmUpload() {
    $(this.fcmServiceAccountFormTarget).find("input[type=submit]").prop("disabled", false)
  }

  #upsertCertificate(certificate) {
    if (!this.hasCertificatesTarget || !certificate?.html) return

    const certificates = $(this.certificatesTarget)
    certificates.find(".no-certificates").remove()

    const existing = certificates.find(`#${certificate.environment}-certificate`).closest(".cert")
    if (existing.length) {
      existing.replaceWith(certificate.html)
    } else {
      certificates.append(certificate.html)
    }

    this.#applyFcmStatusClasses()
  }

  #showApnsUploadSuccess(message) {
    if (!this.hasApnsUploadErrorsTarget) return

    this.#showUploadSuccess(this.apnsUploadErrorsTarget, message)
  }

  #showApnsUploadError(message) {
    if (!this.hasApnsUploadErrorsTarget) return

    this.#showUploadError(this.apnsUploadErrorsTarget, message)
  }

  #upsertFcmCredential(credential) {
    if (!this.hasFcmCredentialsTarget || !credential?.html) return

    const credentials = $(this.fcmCredentialsTarget)
    const existing = credentials.find(`#${credential.environment}-fcm-credential`).closest(".cert")

    if (existing.length) {
      existing.replaceWith(credential.html)
    } else {
      credentials.append(credential.html)
    }

    this.#applyFcmStatusClasses()
  }

  #showFcmUploadSuccess(message) {
    if (!this.hasFcmUploadErrorsTarget) return

    this.#showUploadSuccess(this.fcmUploadErrorsTarget, message)
  }

  #showFcmUploadError(message) {
    if (!this.hasFcmUploadErrorsTarget) return

    this.#showUploadError(this.fcmUploadErrorsTarget, message)
  }

  #showUploadSuccess(target, message) {
    this.#hideUploadError(target)

    let successBlock = $(target).find(".form-results-success").first()

    if (!successBlock.length) {
      successBlock = $(`
        <div class="alert form-results-success alert-success content-alert" style="margin-top: 2px">
          <div class="alert-image-border"></div>
          <a class="close overview_close" data-hide="content-alert">Hide</a>
          <strong>Success</strong>
          <div class="content" style="padding-top: 20px;"></div>
        </div>
      `)
      $(target).prepend(successBlock)
      successBlock.find("[data-hide]").on("click", function (event) {
        event.preventDefault()
        $(this).parent().hide()
      })
    }

    successBlock.find(".content").text(message || "Certificate successfully activated")
    successBlock.removeClass("tmp-hide").stop(true, true).hide().fadeIn()
    if (successBlock[0]?.scrollIntoView) {
      successBlock[0].scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  #showUploadError(target, message) {
    $(target).find(".form-results-success").hide()

    let errorBlock = $(target).find(".form-results-errors").first()

    if (!errorBlock.length) {
      errorBlock = $(`
        <div class="alert tmp-hide form-results-errors alert-danger style-error-notification" style="margin-top: 10px">
          <span class="error-notification-icon" aria-hidden="true"></span>
          <div class="content"></div>
          <div class="wrap-error-nitification-close">
            <a href="" class="close" data-dismiss="alert">Hide</a>
          </div>
        </div>
      `)
      $(target).append(errorBlock)
    }

    errorBlock.find(".content").empty()
      .append($("<strong>").attr("style", "font-weight:bold !important;").text("Error"))
      .append(document.createTextNode(` ${message}`))
    errorBlock.removeClass("tmp-hide").stop(true, true).hide().fadeIn()
  }

  #hideUploadError(target) {
    if (!target) return

    $(target).find(".form-results-errors, .form-results-success").hide()
  }

  #initFileBrowse() {
    this.element.querySelectorAll("input.fileName").forEach((input) => {
      $(input).css("cursor", "default")
      $(input).on("click.notifierSettings", function () {
        $(this).parents(".upload").find("input.inputFile").click()
      })
    })

    this.element.querySelectorAll("input.inputFile").forEach((input) => {
      $(input).on("change.notifierSettings", function () {
        const file = this.files?.[0]?.name || $(this).val().replace(/^.*[\\/]/, "")
        $(this).parents(".upload").find("input.fileName").val(file)
      })
    })
  }

  #initCollapseCookies() {
    $.cookie.json = true
    const cookieName = "message_setting_blocks"
    let openBlocks = $.cookie(cookieName)

    if (openBlocks == null || openBlocks === "") {
      openBlocks = ["#apns"]
    } else {
      openBlocks = openBlocks.split(",")
    }

    openBlocks.forEach((selector) => {
      $(selector).closest(".panel-collapse").collapse("show")
    })

    this.panelCollapseTargets.forEach((panel) => {
      $(panel).on("shown.bs.collapse.notifierSettings hidden.bs.collapse.notifierSettings", () => {
        this.#saveCollapseCookies(cookieName)
      })
    })
  }

  #saveCollapseCookies(cookieName) {
    const open = []
    this.element.querySelectorAll("a[data-toggle='collapse']").forEach((link) => {
      if (!$(link).hasClass("collapsed")) open.push($(link).attr("data-target"))
    })
    $.cookie(cookieName, open)
  }

  #applyFcmStatusClasses() {
    this.element.querySelectorAll(".present").forEach((presentBlock) => {
      const statusEl = presentBlock.querySelector("#PushSettingsStatus")
      if (!statusEl) return
      const content = statusEl.textContent.trim().toLowerCase()

      presentBlock.classList.remove("active", "inactive")
      statusEl.classList.remove("active", "inactive")

      if (content === "inactive") {
        presentBlock.classList.add("inactive")
        statusEl.classList.add("inactive")
      } else if (content === "active") {
        presentBlock.classList.add("active")
        statusEl.classList.add("active")
      }
    })
  }
}
